"use client";
export const dynamic = "force-dynamic";

import {
  Database,
  Target,
  Share2,
  Lock,
  Clock,
  Cookie,
  BarChart3,
  UserCog,
  Users,
  KeyRound,
  ShieldAlert,
  Globe2,
  Brain,
  CreditCard,
  AlertTriangle,
  MessageSquare,
  Link2,
  FileText,
  ServerCog,
  Cloud,
  Cpu,
  UserPlus,
  Image as ImageIcon,
  Plug,
  Webhook,
  Trash2,
  Briefcase,
  AlertOctagon,
  CalendarClock,
  LifeBuoy,
} from "lucide-react";
import LegalLayout, { LegalList, type LegalSection } from "@/components/legal/LegalLayout";

const sections: LegalSection[] = [
  {
    id: "data-collected",
    title: "داده‌های جمع‌آوری‌شده",
    icon: Database,
    highlight:
      "ما تنها داده‌هایی را جمع‌آوری می‌کنیم که برای ارائه و بهبود سرویس ضروری است؛ هیچ داده‌ای بدون هدف مشخص ذخیره نمی‌شود.",
    body: (
      <LegalList
        items={[
          <>
            <b>داده‌های هویتی:</b> نام، شماره موبایل، ایمیل و در صورت نیاز، تصویر پروفایل.
          </>,
          <>
            <b>داده‌های اتصال:</b> توکن‌های دسترسی به اینستاگرام، تلگرام، واتس‌اپ و سایر پلتفرم‌های
            متصل.
          </>,
          <>
            <b>محتوای تعامل:</b> پیام‌ها، دایرکت‌ها، کامنت‌ها، وضعیت گفتگو و فایل‌های ارسال‌شده توسط
            کاربران شما.
          </>,
          <>
            <b>داده‌های مالی:</b> سوابق پرداخت، فاکتورها و وضعیت اشتراک. اطلاعات کارت بانکی هرگز نزد
            ما ذخیره نمی‌شود.
          </>,
          <>
            <b>داده‌های فنی:</b> آی‌پی، نوع دستگاه، مرورگر، سیستم‌عامل، لاگ ورود و صفحات مشاهده‌شده.
          </>,
          <>
            <b>داده‌های پردازشی:</b> رفتار کاربر در داشبورد، صفحات بازدیدشده و میزان تعامل با
            ابزارها.
          </>,
          <>
            <b>داده‌های ارتباطی پشتیبانی:</b> محتوای تیکت‌ها، چت پشتیبانی و یادداشت‌های مرتبط با
            حساب شما.
          </>,
        ]}
      />
    ),
  },
  {
    id: "purpose",
    title: "هدف استفاده از داده‌ها",
    icon: Target,
    body: (
      <LegalList
        items={[
          "ارائه و اجرای پایدار سرویس‌های ترندی اسمارت.",
          "بهبود کیفیت خدمات، رفع باگ و توسعه قابلیت‌های تازه.",
          "تحلیل آماری، گزارش‌گیری و سنجش عملکرد ابزارها.",
          "ارائه پیشنهادهای شخصی‌سازی‌شده، پلن‌های متناسب و آموزش‌های مرتبط.",
          "تأمین امنیت حساب کاربر و جلوگیری از سوءاستفاده، تقلب یا اسپم.",
          "اطلاع‌رسانی‌های ضروری مرتبط با سرویس، صورتحساب و وضعیت اتصال‌ها.",
        ]}
      />
    ),
  },
  {
    id: "sharing",
    title: "اشتراک‌گذاری داده‌ها",
    icon: Share2,
    highlight: "ما داده‌های شما را نمی‌فروشیم. هرگز.",
    body: (
      <LegalList
        items={[
          "ارائه‌دهندگان زیرساخت سرویس مانند درگاه پرداخت، سرویس پیامک و میزبانی ابری — تنها در حد لازم برای اجرای سرویس.",
          "الزامات قانونی، احکام قضایی یا درخواست رسمی مراجع ذی‌صلاح جمهوری اسلامی ایران.",
          "در صورت ادغام یا انتقال مالکیت شرکت، با حفظ کامل تعهدات حریم خصوصی نسبت به کاربران.",
          "با اجازه صریح خود کاربر، در قالب اتصال به سرویس‌های شخص ثالث (مانند ابزارهای CRM یا تحلیل).",
        ]}
      />
    ),
  },
  {
    id: "storage-security",
    title: "ذخیره‌سازی و امنیت داده‌ها",
    icon: Lock,
    body: (
      <LegalList
        items={[
          "تمامی داده‌های حساس به‌صورت رمزنگاری‌شده در حالت ذخیره (at-rest) و انتقال (in-transit با TLS) نگهداری می‌شوند.",
          "بخشی از داده‌ها روی سرورهای داخل ایران و بخشی روی سرورهای خارج از کشور ذخیره می‌شوند.",
          "دسترسی به داده‌ها بر اساس اصل حداقل دسترسی (Least Privilege) و با احراز هویت دومرحله‌ای کنترل می‌شود.",
          "نظارت مداوم، لاگ‌برداری و سیستم تشخیص نفوذ برای شناسایی فعالیت‌های مشکوک به‌کار گرفته می‌شود.",
          "رمز عبور کاربران به‌صورت hash شده (با الگوریتم‌های مقاوم) ذخیره می‌شود و قابل بازیابی نیست.",
        ]}
      />
    ),
  },
  {
    id: "retention",
    title: "مدت زمان نگهداری داده‌ها",
    icon: Clock,
    body: (
      <LegalList
        items={[
          "تا زمان فعال بودن حساب کاربری، داده‌ها برای ارائه سرویس نگهداری می‌شوند.",
          "پس از حذف حساب، داده‌های ضروری مانند سوابق مالی طبق قوانین بایگانی نگهداری و سایر داده‌ها حذف می‌شوند.",
          "نسخه‌های پشتیبان (Backup) ممکن است تا حداکثر ۹۰ روز پس از حذف نزد ما باقی بمانند و سپس به‌صورت کامل پاک شوند.",
          "لاگ‌های امنیتی به مدت محدود (معمولاً ۱۸۰ روز) برای مقاصد امنیتی نگهداری می‌شوند.",
        ]}
      />
    ),
  },
  {
    id: "cookies",
    title: "کوکی‌ها و فناوری‌های مشابه",
    icon: Cookie,
    body: (
      <LegalList
        items={[
          "کوکی‌های ضروری برای حفظ نشست (Session)، احراز هویت و امنیت کاربر استفاده می‌شوند.",
          "کوکی‌های عملکردی برای ذخیره ترجیحات کاربر مانند تم تاریک/روشن و زبان.",
          "کوکی‌های تحلیلی برای سنجش رفتار کاربر و بهبود تجربه — قابلیت غیرفعال‌سازی توسط کاربر.",
          "غیرفعال‌سازی کوکی‌ها ممکن است باعث اختلال در بخشی از خدمات یا نیاز به ورود مجدد شود.",
        ]}
      />
    ),
  },
  {
    id: "analytics",
    title: "ابزارهای تحلیل داده",
    icon: BarChart3,
    body: (
      <LegalList
        items={[
          "از Google Analytics برای تحلیل ترافیک عمومی وب‌سایت استفاده می‌کنیم.",
          "از ابزارهای داخلی برای پایش رفتار در داشبورد و بهبود فلوهای کاربری بهره می‌گیریم.",
          "تمامی داده‌های تحلیلی پیش از پردازش، تجمیع و تا حد امکان ناشناس‌سازی می‌شوند.",
          "کاربر می‌تواند از طریق تنظیمات حساب کاربری، ترجیحات تحلیلی خود را مدیریت کند.",
        ]}
      />
    ),
  },
  {
    id: "user-rights",
    title: "حقوق کاربر",
    icon: UserCog,
    highlight: "شما در هر لحظه مالک داده‌های خود هستید.",
    body: (
      <LegalList
        items={[
          "درخواست دسترسی به داده‌های شخصی ذخیره‌شده.",
          "درخواست اصلاح، به‌روزرسانی یا تکمیل اطلاعات.",
          "درخواست حذف داده‌های شخصی (به جز موارد الزام قانونی).",
          "درخواست خروجی داده‌ها (Data Export) در قالب قابل خواندن.",
          "اعتراض یا محدود کردن نوع پردازش‌های انجام‌شده روی داده‌ها.",
          "تمامی این درخواست‌ها در چارچوب قوانین، حداکثر ظرف ۱۴ روز کاری بررسی و اجرا می‌شوند.",
        ]}
      />
    ),
  },
  {
    id: "staff-access",
    title: "دسترسی کارکنان",
    icon: Users,
    body: (
      <LegalList
        items={[
          "تنها کارکنان مجاز و آموزش‌دیده در بخش‌های پشتیبانی، فنی و امنیت به داده‌ها دسترسی دارند.",
          "هر دسترسی به داده‌های کاربران لاگ شده و قابل ردیابی است.",
          "تمامی کارکنان توافق‌نامه عدم افشای اطلاعات (NDA) امضا می‌کنند.",
        ]}
      />
    ),
  },
  {
    id: "sensitive",
    title: "داده‌های حساس",
    icon: KeyRound,
    body: (
      <LegalList
        items={[
          "ترندی اسمارت هرگز رمز عبور شبکه‌های اجتماعی شما را ذخیره نمی‌کند؛ تمام اتصال‌ها از طریق توکن‌های امن OAuth انجام می‌شود.",
          "اطلاعات کامل کارت بانکی نزد ما نگهداری نمی‌شود؛ پرداخت‌ها از طریق درگاه‌های معتبر بانکی صورت می‌گیرد.",
          "اطلاعات بیومتریک یا داده‌های حساس مانند مذهب، نژاد و گرایش سیاسی هیچ‌گاه جمع‌آوری نمی‌شود.",
        ]}
      />
    ),
  },
  {
    id: "user-responsibility",
    title: "مسئولیت کاربر در نگهداری اطلاعات",
    icon: ShieldAlert,
    body: (
      <LegalList
        items={[
          "حفظ محرمانگی نام کاربری، رمز عبور و توکن‌های دسترسی بر عهده کاربر است.",
          "در صورت مشکوک شدن به افشای اطلاعات، کاربر موظف است سریعاً رمز عبور را تغییر داده و به پشتیبانی اطلاع دهد.",
          "ترندی اسمارت مسئولیتی در قبال خسارات ناشی از اهمال کاربر در نگهداری اطلاعات ندارد.",
        ]}
      />
    ),
  },
  {
    id: "international",
    title: "انتقال بین‌المللی داده‌ها",
    icon: Globe2,
    body: (
      <LegalList
        items={[
          "بخشی از داده‌ها ممکن است روی سرورهای خارج از ایران پردازش یا ذخیره شوند.",
          "این انتقال‌ها تنها با ارائه‌دهندگانی انجام می‌شود که استانداردهای امنیتی و حریم خصوصی معتبر را رعایت می‌کنند.",
          "استفاده از خدمات به‌منزله پذیرش این انتقال است.",
        ]}
      />
    ),
  },
  {
    id: "anonymous",
    title: "استفاده از داده‌های ناشناس",
    icon: Brain,
    body: (
      <LegalList
        items={[
          "از داده‌های تجمیع‌شده و کاملاً ناشناس برای آموزش الگوریتم‌های هوش مصنوعی استفاده می‌کنیم.",
          "هیچ داده‌ای که هویت کاربر را قابل شناسایی کند در فرآیند آموزش به کار نمی‌رود.",
          "گزارش‌های آماری منتشرشده هرگز شامل اطلاعات شخصی کاربران نخواهد بود.",
        ]}
      />
    ),
  },
  {
    id: "financial-records",
    title: "سوابق مالی",
    icon: CreditCard,
    body: (
      <LegalList
        items={[
          "سوابق تراکنش‌های مالی طبق قوانین مالیاتی و تجاری کشور حتی پس از حذف حساب نگهداری می‌شوند.",
          "این داده‌ها صرفاً برای ارائه به مراجع قانونی، صدور فاکتور و رفع اختلافات مالی استفاده می‌شوند.",
        ]}
      />
    ),
  },
  {
    id: "incidents",
    title: "رخدادهای امنیتی",
    icon: AlertTriangle,
    body: (
      <LegalList
        items={[
          "در صورت وقوع نقص امنیتی مهم که داده‌های کاربران را تحت تأثیر قرار دهد، در اسرع وقت کاربران ذی‌نفع را مطلع می‌کنیم.",
          "شامل توضیح ماهیت رخداد، داده‌های متأثر و اقدامات اصلاحی انجام‌شده.",
          "گزارش به مراجع نظارتی ذی‌صلاح در صورت الزام قانونی.",
        ]}
      />
    ),
  },
  {
    id: "messages",
    title: "ذخیره‌سازی پیام‌ها و گفتگوها",
    icon: MessageSquare,
    body: (
      <LegalList
        items={[
          "پیام‌ها و گفتگوها صرفاً برای نمایش در داشبورد، آرشیو، جستجو و آموزش ربات شخصی شما ذخیره می‌شوند.",
          "ترندی اسمارت بدون اجازه صریح کاربر، محتوای گفتگوها را برای اهداف دیگر استفاده نمی‌کند.",
          "کاربر می‌تواند در هر زمان حذف گفتگوها یا غیرفعال‌سازی آرشیو را درخواست کند.",
        ]}
      />
    ),
  },
  {
    id: "connected-accounts",
    title: "حساب‌های متصل",
    icon: Link2,
    body: (
      <LegalList
        items={[
          "اطلاعات حساب‌های متصل (اینستاگرام، تلگرام، واتس‌اپ) فقط برای مدیریت تعاملات کاربر استفاده می‌شود.",
          "هرگز بدون اجازه شما، پست، استوری یا پیامی از طرف حساب شما ارسال نمی‌شود.",
          "در هر زمان می‌توانید اتصال هر حساب را قطع کنید؛ پس از قطع اتصال، توکن‌ها فوراً ابطال می‌شوند.",
        ]}
      />
    ),
  },
  {
    id: "user-content",
    title: "مسئولیت محتوای ارسالی",
    icon: FileText,
    body: (
      <LegalList
        items={[
          "مسئولیت کلیه پیام‌ها، فایل‌ها، رسانه‌ها و اطلاعات ارسال‌شده از طریق پلتفرم بر عهده کاربر است.",
          "ترندی اسمارت در قبال محتوای کاربران پاسخگو نیست، اما در صورت گزارش محتوای غیرقانونی اقدام می‌کند.",
        ]}
      />
    ),
  },
  {
    id: "system-logs",
    title: "لاگ‌های سیستمی",
    icon: ServerCog,
    body: (
      <LegalList
        items={[
          "لاگ‌های شامل آی‌پی، نوع دستگاه، زمان ورود و عملیات انجام‌شده ذخیره می‌شوند.",
          "کاربرد این لاگ‌ها در حوزه امنیت، رفع باگ، شناسایی سوءاستفاده و ممیزی است.",
          "دسترسی به لاگ‌ها محدود به تیم امنیت و فنی است.",
        ]}
      />
    ),
  },
  {
    id: "cloud-backup",
    title: "خدمات ابری و بک‌آپ",
    icon: Cloud,
    body: (
      <LegalList
        items={[
          "داده‌ها ممکن است روی خدمات ابری معتبر و ایمن میزبانی شوند.",
          "نسخه‌های پشتیبان به‌صورت روزانه گرفته شده و رمزنگاری می‌شوند.",
          "بک‌آپ‌ها ممکن است حتی پس از حذف حساب، تا مدت محدودی نگهداری شوند و سپس کاملاً حذف شوند.",
        ]}
      />
    ),
  },
  {
    id: "automation",
    title: "پردازش خودکار و تصمیم‌گیری ماشینی",
    icon: Cpu,
    body: (
      <LegalList
        items={[
          "بخشی از پردازش‌ها از جمله پاسخ‌گویی هوشمند، دسته‌بندی پیام‌ها و پیشنهادها به‌صورت خودکار توسط الگوریتم انجام می‌شود.",
          "کاربر می‌تواند در هر زمان درخواست بازنگری انسانی روی تصمیمات خودکار را ارائه دهد.",
          "تصمیمات حیاتی (مانند تعلیق حساب) همواره با بازبینی انسانی همراه است.",
        ]}
      />
    ),
  },
  {
    id: "third-party-data",
    title: "داده‌های اشخاص ثالث",
    icon: UserPlus,
    body: (
      <LegalList
        items={[
          "اگر کاربر داده‌های اشخاص دیگر مانند شماره موبایل یا ایمیل را وارد کند، مسئولیت حقوقی کسب رضایت آن افراد بر عهده کاربر است.",
          "داده‌های مشتریان نهایی شما (افرادی که با حساب شما تعامل دارند) صرفاً برای ارائه خدمات به شما پردازش می‌شوند.",
          "ما داده‌های مشتریان نهایی شما را با هیچ کاربر دیگری به اشتراک نمی‌گذاریم.",
        ]}
      />
    ),
  },
  {
    id: "media",
    title: "داده‌های چندرسانه‌ای",
    icon: ImageIcon,
    body: (
      <LegalList
        items={[
          "فایل‌های عکس، ویدئو، صدا و سند که از طریق ترندی اسمارت منتقل می‌شوند، صرفاً برای ارائه خدمات ذخیره و پردازش می‌شوند.",
          "حداکثر حجم و فرمت فایل‌های مجاز در مستندات پلن اشتراکی شما مشخص شده است.",
          "فایل‌های بزرگ ممکن است پس از مدتی از حافظه فعال خارج و در آرشیو سرد منتقل شوند.",
        ]}
      />
    ),
  },
  {
    id: "third-party-services",
    title: "سرویس‌های شخص ثالث",
    icon: Plug,
    body: (
      <LegalList
        items={[
          "ممکن است به سرویس‌های شخص ثالث مانند درگاه‌های پرداخت، سرویس پیامک، نقشه و ابزارهای تحلیل متصل شویم.",
          "سیاست‌های حریم خصوصی این سرویس‌ها مستقل از ترندی اسمارت است و ما در قبال آن‌ها مسئولیتی نداریم.",
          "پیش از اتصال به هر سرویس شخص ثالث، اطلاعات لازم به کاربر اطلاع داده می‌شود.",
        ]}
      />
    ),
  },
  {
    id: "official-api",
    title: "وابستگی به API رسمی",
    icon: Webhook,
    body: (
      <LegalList
        items={[
          "اتصال و جمع‌آوری داده‌ها تنها از طریق API رسمی پلتفرم‌ها (Meta Graph API، Telegram Bot API و WhatsApp Cloud API) انجام می‌شود.",
          "ترندی اسمارت از روش‌های غیررسمی، Scraping یا اتوماسیون مرورگری استفاده نمی‌کند.",
          "هرگونه تغییر در سیاست API این پلتفرم‌ها ممکن است بر دسترسی به داده‌ها تأثیر بگذارد.",
        ]}
      />
    ),
  },
  {
    id: "user-management",
    title: "مدیریت داده‌ها توسط کاربر",
    icon: Trash2,
    body: (
      <LegalList
        items={[
          "از طریق بخش پشتیبانی یا تنظیمات حساب می‌توانید درخواست حذف کامل داده‌ها را ثبت کنید.",
          "درخواست حذف حساب ظرف ۳۰ روز کاری اجرا می‌شود؛ در این بازه می‌توانید درخواست را لغو کنید.",
          "خروجی کامل داده‌های شما در قالب JSON / CSV قابل دریافت است.",
        ]}
      />
    ),
  },
  {
    id: "internal-use",
    title: "استفاده داخلی تیم",
    icon: Briefcase,
    body: (
      <LegalList
        items={[
          "داده‌ها ممکن است به‌صورت محدود و ناشناس برای آموزش تیم پشتیبانی، بهبود فرآیندها و QA استفاده شوند.",
          "هیچ داده شخصی قابل شناسایی در ارائه‌ها، گزارش‌های داخلی یا جلسات تیم استفاده نمی‌شود.",
        ]}
      />
    ),
  },
  {
    id: "limitations",
    title: "محدودیت‌های امنیتی",
    icon: AlertOctagon,
    body: (
      <LegalList
        items={[
          "هیچ پلتفرم آنلاینی نمی‌تواند حفاظت ۱۰۰٪ در برابر حملات غیرقابل پیش‌بینی را تضمین کند.",
          "ما متعهدیم بهترین استانداردها را رعایت کنیم، اما کاربر نیز باید اقدامات احتیاطی شخصی را انجام دهد.",
        ]}
      />
    ),
  },
  {
    id: "effective-date",
    title: "تاریخ اجرا و بازنگری",
    icon: CalendarClock,
    body: (
      <LegalList
        items={[
          "این سیاست از تاریخ انتشار در وب‌سایت ترندی اسمارت لازم‌الاجراست.",
          "در صورت تغییرات اساسی، از طریق ایمیل و اعلان داشبورد به کاربران اطلاع‌رسانی خواهد شد.",
          "تنها آخرین نسخه منتشر شده در وب‌سایت معتبر است.",
        ]}
      />
    ),
  },
  {
    id: "support",
    title: "ارتباط با پشتیبانی",
    icon: LifeBuoy,
    body: (
      <LegalList
        items={[
          <>
            برای پرسش‌ها یا درخواست‌های مرتبط با حریم خصوصی با ایمیل{" "}
            <a
              className="text-primary underline-offset-4 hover:underline"
              href="mailto:privacy@trendi-smart.ir"
            >
              privacy@trendi-smart.ir
            </a>{" "}
            در ارتباط باشید.
          </>,
          "از طریق بخش پشتیبانی داشبورد می‌توانید تیکت اختصاصی با موضوع «حریم خصوصی» ثبت کنید.",
          "زمان پاسخ‌گویی استاندارد: حداکثر ۴۸ ساعت کاری.",
        ]}
      />
    ),
  },
];

export default function PrivacyPage() {
  return (
    <LegalLayout
      kind="privacy"
      eyebrow="سیاست حفظ حریم خصوصی"
      title="حریم خصوصی شما، اولویت ماست."
      intro="این سند توضیح می‌دهد ترندی اسمارت چه داده‌هایی جمع‌آوری می‌کند، چگونه از آن‌ها محافظت می‌کند و شما به‌عنوان کاربر چه حقوقی دارید. استفاده از خدمات به منزله پذیرش این سیاست است."
      updatedAt="۱۴۰۳/۰۲/۱۸"
      effectiveAt="۱۴۰۳/۰۲/۲۰"
      sections={sections}
      sisterTitle="قوانین و مقررات"
      sisterHref="/terms"
      sisterDescription="شرایط و ضوابط الزام‌آور استفاده از سرویس‌های ترندی اسمارت."
    />
  );
}
